import config from '../config';

const GTAG_URL = 'https://www.googletagmanager.com/gtag/js';

// Flipped by init() once gtag.js is installed; every other method no-ops until then.
let enabled = false;

// Guards against duplicate hits when the router replaces a path with itself.
let lastPath = null;

/**
 * Turn a route path into a readable page title so the GA4 Pages report is
 * legible on its own, e.g.
 * `/from-hkust/bus-stops/north-gate` -> `From HKUST - Bus Stops - North Gate`
 * @param {String} path
 * @returns {String}
 */
const titleFromPath = (path) => {
	const segments = (path || '/').split('/').filter(Boolean);

	if (!segments.length) {
		return 'Home';
	}

	return segments
		.map((segment) => segment
			.split('-')
			.map((word) => (word ? `${word[0].toUpperCase()}${word.slice(1)}` : word))
			.join(' '))
		.join(' - ')
		.replace(/Hkust/g, 'HKUST');
};

/**
 * Drop empty values so GA4 never records `undefined` as a dimension value
 * @param {Object} params
 * @returns {Object}
 */
const clean = (params) => {
	return Object.keys(params || {}).reduce((acc, key) => {
		const value = params[key];

		if (value !== undefined && value !== null && value !== '') {
			acc[key] = value;
		}

		return acc;
	}, {});
};

const AnalyticsHelper = {
	/**
	 * Install gtag.js. No-op unless the active environment config carries a
	 * measurement id (only production does), so development, testing and UAT
	 * traffic never reaches Google Analytics.
	 * @returns {void}
	 */
	init: () => {
		const measurementId = config.gaMeasurementId;

		if (enabled || !measurementId || typeof window === 'undefined') {
			return;
		}

		window.dataLayer = window.dataLayer || [];

		// gtag pushes its `arguments` object verbatim - a rest-parameter wrapper
		// would push a plain array and gtag.js would ignore the hit.
		function gtag() {
			window.dataLayer.push(arguments);
		}

		window.gtag = gtag;

		gtag('js', new Date());
		// The SPA sends its own page_view on every route change.
		gtag('config', measurementId, { send_page_view: false });

		const script = document.createElement('script');
		script.async = true;
		script.src = `${GTAG_URL}?id=${measurementId}`;
		document.head.appendChild(script);

		enabled = true;
	},

	/**
	 * Send a page_view for a client-side route change
	 * @param {String} path router pathname, without the `/eta/app` basename
	 * @param {String} [title] defaults to a title derived from the path
	 * @returns {void}
	 */
	pageView: (path, title) => {
		if (!enabled || !window.gtag || path === lastPath) {
			return;
		}

		lastPath = path;

		window.gtag('event', 'page_view', {
			page_title: title || titleFromPath(path),
			page_location: window.location.href,
			page_path: path
		});
	},

	/**
	 * Send a custom event
	 * @param {String} name GA4 event name, snake_case
	 * @param {Object} [params] event parameters; register these as custom
	 *                          dimensions in GA4 to report on them
	 * @returns {void}
	 */
	track: (name, params) => {
		if (!enabled || !window.gtag || !name) {
			return;
		}

		window.gtag('event', name, clean(params));
	},

	/**
	 * Whether gtag.js was installed for this environment
	 * @returns {Boolean}
	 */
	isEnabled: () => enabled
};

export default AnalyticsHelper;
